import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { customStyles } from '../../../constants/form';
import { CloseImage } from '../../user/styled';
import Modal from 'react-modal';
import closeIcon from '../../../assets/images/token-modal/close.png';
import { Col, Row } from 'react-bootstrap';
import { Button } from './Button';
import { useTextLanguage } from '../../../hooks/useShared';
import { useSelector, useDispatch } from 'react-redux';
import { trimLowerCase } from '../../../helpers/shared';
import styled from 'styled-components';
import { setIBV } from '../../../redux/actions/base';

/**
 * Component modal of confirmation to tier
 * @returns
 */
export const ModalQuestion = ({
  modalIsOpen,
  setModal,
  onChangeStep,
  message,
  product,
  children
}) => {
  const { t } = useTranslation()

  return (
    <Modal
      ariaHideApp={ false }
      isOpen={ modalIsOpen }
      onAfterClose={ () => setModal(false) }
      style={ customStyles }
    >
      <CloseImage
        alt=""
        onClick={ () => setModal(false) }
        src={ closeIcon }
        name="image_close"
      />
      <p className="mt-4">{ message }</p>
      {product && children }
      <Row className="justify-content-center m-0">
        <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
          <Button
            className="btn mb-0"
            type="submit"
            color="first"
            onClick={ onChangeStep }
            name="btn_modal"
          >
            {product ? t("common.requestButton") : t("common.submit")}
          </Button>
        </Col>
        <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
          <Button
            className="btn mb-0"
            type="button"
            color="second"
            onClick={() => setModal(false)}
            name="btn_modal_cancel"
          >
            {t("common.cancel")}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

ModalQuestion.prototype = {
  modalIsOpen: PropTypes.bool.isRequired,
  setModal: PropTypes.func.isRequired,
  onChangeStep: PropTypes.func.isRequired
};

/**
 * Component for the Opt In modal of confirmation, in the final tier, before migration
 * @returns
 */
export const OptInModal = ({
  onChangeStep,
  modalIsOpen,
  setModal,
  handleOpenIbv
}) => {
  const { t } = useTranslation()
  const { languageText } = useTextLanguage()
  const currentLanguage = languageText?.split('_')[1]
  const { optInModalInfo } = useSelector(store => store.base)
  let info = optInModalInfo?.filter(infoSet => infoSet.language_code === trimLowerCase(currentLanguage))[0]
  let conditions = info ? Object?.keys(info)?.filter(key => key?.includes('text_'))
    .map(text => info[text]) : null
  const [acceptedConditions, setAcceptedConditions] = React.useState({})
  const { isIBVVerified, requestWidgetURL } = useSelector(state => state.base.ibv) || {}
  const dispatch = useDispatch()

  React.useEffect(() => {
    setTimeout(() => {
      if (conditions && Object.keys(acceptedConditions).length === 0) {
        let initialAcceptedConditions = {};
        conditions.forEach((_, idx) => {
          initialAcceptedConditions[`condition_${idx + 1}`] = true;
        });
        setAcceptedConditions(initialAcceptedConditions);
      }
    }, 1100);

  }, [conditions, acceptedConditions]);
  
  const [enabled, setEnabled] = React.useState(false)
  const acceptedConditionsValues = Object.values(acceptedConditions)
  React.useEffect(() => {
    const isEnabled = Object.values(acceptedConditions)?.length && Object.values(acceptedConditions).every(condition => condition);
    setEnabled(isEnabled);
  }, [acceptedConditions, acceptedConditionsValues]);

  const handleClose = () => {
    setAcceptedConditions(prevConditions => {
      let resettedConditions = {}
      Object.keys(prevConditions).forEach(condition => {
        resettedConditions[condition] = false
      })
      return resettedConditions
    })
    setModal(false)
    const params = {
      pendingVerification: false
    }
    dispatch(setIBV(params))
  }

  /* Should send form or trigger IBV? */
  const shouldValidate = (requestWidgetURL?.length && !isIBVVerified)

  return (
    <Modal
      ariaHideApp={ false }
      isOpen={modalIsOpen}
      onAfterClose={ handleClose }
      style={ customStyles }
    >
      <CloseImage
        alt=""
        onClick={handleClose}
        src={ closeIcon }
        name="image_close"
      />

      {
        conditions ? conditions.map((condition, idx) => {
          return (
            <Condition 
              condition={condition}
              value={acceptedConditions[`condition_${idx + 1}`]}
              idx={idx}
              numberOfCondition={conditions?.length + 1}
              setAcceptedConditions={setAcceptedConditions}
              /*'key' is just for avoiding error in console. Remember using indexex as 'key' is a bad idea when trying any further logic with this array's elements */
              key={idx} 
            />
          )
        }) : null
      }
      {
        conditions ? (
          <>
            <p className="mt-2" dangerouslySetInnerHTML={{__html: info?.additional_text}} />  
            {/* {product && children } */}
            <Row className="justify-content-center m-0">
              <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
                <Button
                  className="btn mb-0"
                  type="submit"
                  color="first"
                  disabled={!enabled}
                  onClick={ shouldValidate ? handleOpenIbv : onChangeStep }
                  name="btn_modal"
                >
                  {t("common.accept")}
                </Button>
              </Col>
              <Col className="p-2 mt-2 mb-2" xs={12} md={6}>
                <Button
                  className="btn mb-0"
                  type="button"
                  color="second"
                  onClick={ handleClose }
                  name="btn_modal_cancel"
                >
                  {t("common.cancel")}
                </Button>
              </Col>
            </Row>
          </>
        ) : null
      }
      {/* Warning message if opt-in info could be fetcched */}
      {
        !conditions ? (
          <div className="alert alert-info mt-4" role="alert">
            { t("modalOptIn.termsNotFound_1") } <br />
            { t("modalOptIn.termsNotFound_2") }
          </div>
        ) : null
      }
 
    </Modal>
  );
}

const Condition = ({
  condition,
  idx,
  numberOfCondition,
  setAcceptedConditions,
  value
}) => {
  const last = (Number(idx) + 1) === Number(numberOfCondition)

  const handleCondition = (e) => {
    setAcceptedConditions(prevConditions => {
      return {
        ...prevConditions,
        [`condition_${Number(idx) + 1}`]: e.target.checked
      }
    })
  }

  return (
    <ConditionDiv>
      <input 
        type='checkbox' name={`condition_${idx}`} onClick={handleCondition} 
        defaultChecked={value}
      />
      <label
        className={ last ? 'last' : '' }
        htmlFor={`condition_${idx}`}
        dangerouslySetInnerHTML={{ __html: condition }}
      />
    </ConditionDiv>
  )
}

const ConditionDiv = styled.div`
  margin-top:.55rem;
  display:flex;
  gap: .55rem;
  align-items: baseline;
  & > input {
    position: relative;
    top: .55rem;
  }
  .last p {
    margin-bottom: 0px;
  }
`